import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-safe:active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground font-semibold shadow-elevation-1 hover:bg-primary/92',
                destructive:
                    'bg-destructive text-destructive-foreground font-semibold shadow-elevation-1 hover:bg-destructive/90',
                outline:
                    'border border-border bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground',
                secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90',
                tint: 'border border-brand/15 bg-brand-subtle text-brand font-semibold shadow-none hover:bg-brand-subtle/80',
                surface:
                    'border border-border bg-card text-foreground shadow-none hover:bg-surface-muted',
                ghost: 'hover:bg-accent hover:text-accent-foreground',
                ghostBrand: 'text-muted-foreground hover:bg-brand-subtle hover:text-brand',
                ghostDestructive:
                    'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
                link: 'text-primary underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-10 px-4 py-2.5',
                sm: 'h-8 rounded-md px-3 text-xs',
                lg: 'h-11 px-6 text-sm',
                xl: 'h-12 px-6 text-base',
                icon: 'h-10 w-10',
                iconSm: 'h-8 w-8 rounded-md p-0',
                iconXs: 'h-7 w-7 rounded-md p-0 [&_svg]:size-3.5',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        );
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
